import { useEffect, useRef, type MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import { ConnectionHandler } from '@sendbird/chat';

import type { RootDispatch } from '../../configuration/setup/store';
import { useRequestTokenMutation } from '../../services/chatAdministrationApi';
import { chatError, chatErrorResetted, chatInitialized, userIdFetched } from '../../features/chat/chatSlice';
import { sendbird } from '../client/sendbird';
import { useChatErrorHandlers } from './useChatErrorHandlers';
import { useChannelListHandler } from './useChannelListHandler';
import { useMessageEventHandler } from './useMessageEventHandler';

type CancellationCheck = () => boolean;
type RequestToken = ReturnType<typeof useRequestTokenMutation>[0];
type HandleError = ReturnType<typeof useChatErrorHandlers>['handleError'];

type InitializationDependencies = {
    dispatch: RootDispatch;
    requestToken: RequestToken;
    handleError: HandleError;
    initializingRef: MutableRefObject<boolean>;
    isCancelled: CancellationCheck;
};

const initializeSendbird = async ({
    dispatch,
    requestToken,
    handleError,
    initializingRef,
    isCancelled,
}: InitializationDependencies) => {
    if (initializingRef.current || isCancelled()) {
        return;
    }

    initializingRef.current = true;

    try {
        dispatch(chatErrorResetted());
        const result = await requestToken().unwrap();

        if (isCancelled()) {
            return;
        }

        if (result.userId.startsWith('driver_')) {
            dispatch(
                chatError({
                    errorType: 'DRIVER_CHAT_NOT_SUPPORTED',
                })
            );
            return;
        }

        await sendbird.connect(result.userId, result.token);

        if (isCancelled()) {
            return;
        }

        sendbird.addConnectionHandler(
            result.userId,
            new ConnectionHandler({
                onReconnectSucceeded: () => {
                    dispatch(chatErrorResetted());
                },
            })
        );

        dispatch(userIdFetched(result.userId));
        dispatch(chatInitialized());
    } catch (error) {
        if (!isCancelled()) {
            handleError(error);
        }
    } finally {
        initializingRef.current = false;
    }
};

export const useSendbirdInitialization = () => {
    const [requestToken] = useRequestTokenMutation();
    const dispatch = useDispatch();
    const { handleError } = useChatErrorHandlers();
    const initializingRef = useRef(false);

    useChannelListHandler();
    useMessageEventHandler();

    useEffect(() => {
        let cancelled = false;

        void initializeSendbird({
            dispatch,
            requestToken,
            handleError,
            initializingRef,
            isCancelled: () => cancelled,
        });

        return () => {
            cancelled = true;
        };
    }, []);
};
